/**
 * AdminP1Page
 *
 * P1 admin UI: Verification Requests, Opt-Out Requests, Audit Log.
 * Mounted at /admin/p1. Protected by ProtectedRoute allowedRoles={['admin']}.
 *
 * Intentional design: fast table + inline detail drawer, not pretty-first.
 * 100% i18n via useLanguage(). No overlay/pointer bugs (no Radix Sheet).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, XCircle, EyeOff, Trash2, Flag, RefreshCw, ArrowLeft, Search, QrCode, Share2, Copy, Check, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import GlobalNav from '@/components/GlobalNav';
import { useLanguage } from '@/lib/context';
import AdminBudgetStatsWidget from '@/pages/admin/AdminBudgetStatsWidget';
import AdminBackhaulRateWidget from '@/pages/admin/AdminBackhaulRateWidget';

const API = process.env.REACT_APP_BACKEND_URL;

const T = {
  et: {
    title: 'Admin järjekord',
    tabVerify: 'Kinnitused',
    tabOptout: 'Kustutustaotlused',
    tabAudit: 'Auditilogi',
    tabDemos: 'Demo avamised',
    pending: 'Ootel',
    approvedToday: 'Kinnitatud täna',
    rejectedToday: 'Tagasi lükatud täna',
    processedToday: 'Töödeldud täna',
    filterStatus: 'Staatus',
    filterCountry: 'Riik',
    searchPh: 'Otsi nime / ID järgi…',
    all: 'Kõik',
    approved: 'Kinnitatud',
    rejected: 'Tagasi lükatud',
    processed: 'Töödeldud',
    back: 'Tagasi',
    refresh: 'Värskenda',
    noRows: 'Päringuid ei leitud.',
    company: 'Ettevõte',
    country: 'Riik',
    registry: 'Registrikood',
    submitted: 'Esitatud',
    status: 'Staatus',
    flags: 'Märkid',
    approve: 'Kinnita',
    reject: 'Lükka tagasi',
    hide: 'Peida',
    delete: 'Kustuta',
    banDupes: 'Märgi duplikaadid',
    reasonLabel: 'Põhjus (nõutav)',
    reasonPh: 'Miks lükkad tagasi?',
    confirm: 'Kinnita',
    cancel: 'Tühista',
    requesterEmail: 'Taotleja e-post',
    businessId: 'Äri ID',
    duplicateIds: 'Duplikaadi ID-d (komaga eraldatud)',
    note: 'Märkus',
    actionOk: 'Tegevus sooritatud',
    actionFail: 'Tegevus ebaõnnestus',
    auditAction: 'Tegevus',
    auditEntity: 'Objekt',
    auditActor: 'Administraator',
    auditTime: 'Aeg',
    demosLast24h: 'Viimased 24h',
    demosLast5m: 'Viimased 5 min',
    demosPeakAllTime: 'All-time peak (5m)',
    demosLastUpdate: 'Viimati uuendatud',
    demosLastEvent: 'Viimane sündmus',
    demosUa: 'Brauser',
    demosTotal: 'Kokku',
    demosTopCountry: 'Top riik',
    demosShown: 'Kuvatud',
    demosEmpty: 'QR-skaneeringuid veel pole.',
    demosCountry: 'Riik',
    demosLang: 'Keel',
    demosDevice: 'Seade',
    demosSession: 'Sessioon',
    demosPath: 'Rada',
    peakTitle: 'Tipuaktiivsus!',
    peakSub: 'Uus 5-min rekord',
    shareLive: 'Jaga live vaadet',
    shareLiveTitle: 'Jaga live demo vaadet',
    shareLiveDesc: 'See avalik link avab iga telefoni lugejal ainult lugemiseks demo live vaate. Private admin andmeid ei jagata.',
    shareCopy: 'Kopeeri link',
    shareCopied: 'Kopeeritud!',
    shareRegenerate: 'Genereeri uus',
    shareDownloadQr: 'Laadi QR alla',
    shareExpires: 'Kehtib kuni',
    shareValidFor: 'Kehtiv 24 tundi',
    shareOpen: 'Ava',
  },
  en: {
    title: 'Admin Queue',
    tabVerify: 'Verification',
    tabOptout: 'Opt-out',
    tabAudit: 'Audit log',
    tabDemos: 'Demo opens',
    pending: 'Pending',
    approvedToday: 'Approved today',
    rejectedToday: 'Rejected today',
    processedToday: 'Processed today',
    filterStatus: 'Status',
    filterCountry: 'Country',
    searchPh: 'Search by name / ID…',
    all: 'All',
    approved: 'Approved',
    rejected: 'Rejected',
    processed: 'Processed',
    back: 'Back',
    refresh: 'Refresh',
    noRows: 'No requests found.',
    company: 'Company',
    country: 'Country',
    registry: 'Registry ID',
    submitted: 'Submitted',
    status: 'Status',
    flags: 'Flags',
    approve: 'Approve',
    reject: 'Reject',
    hide: 'Hide listing',
    delete: 'Delete listing',
    banDupes: 'Ban duplicates',
    reasonLabel: 'Reason (required)',
    reasonPh: 'Why reject?',
    confirm: 'Confirm',
    cancel: 'Cancel',
    requesterEmail: 'Requester email',
    businessId: 'Business ID',
    duplicateIds: 'Duplicate IDs (comma-separated)',
    note: 'Note',
    actionOk: 'Done',
    actionFail: 'Action failed',
    auditAction: 'Action',
    auditEntity: 'Entity',
    auditActor: 'Admin',
    auditTime: 'Time',
    demosLast24h: 'Last 24h',
    demosLast5m: 'Last 5 min',
    demosPeakAllTime: 'All-time peak (5m)',
    demosLastUpdate: 'Last updated',
    demosLastEvent: 'Last event',
    demosUa: 'Browser',
    demosTotal: 'Total',
    demosTopCountry: 'Top country',
    demosShown: 'Shown',
    demosEmpty: 'No QR scans yet.',
    demosCountry: 'Country',
    demosLang: 'Lang',
    demosDevice: 'Device',
    demosSession: 'Session',
    demosPath: 'Path',
    peakTitle: 'Peak activity!',
    peakSub: 'All-time 5-min record',
    shareLive: 'Share live view',
    shareLiveTitle: 'Share live demo view',
    shareLiveDesc: 'This public link lets any phone open a read-only live demo view. No private admin data is shared.',
    shareCopy: 'Copy link',
    shareCopied: 'Copied!',
    shareRegenerate: 'Regenerate',
    shareDownloadQr: 'Download QR',
    shareExpires: 'Valid until',
    shareValidFor: 'Valid for 24h',
    shareOpen: 'Open',
  },
  fi: { /* fallback to en */ },
  ru: { /* fallback to en */ },
};

const useT = () => {
  const { language } = useLanguage();
  return T[language] && Object.keys(T[language]).length ? T[language] : T.en;
};

const StatusBadge = ({ value }) => {
  const color = {
    pending: 'bg-amber-100 text-amber-800 border-amber-300',
    approved: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    rejected: 'bg-rose-100 text-rose-800 border-rose-300',
    processed: 'bg-sky-100 text-sky-800 border-sky-300',
  }[value] || 'bg-slate-100 text-slate-700 border-slate-300';
  return <span className={`inline-block px-2 py-0.5 rounded border text-xs font-medium ${color}`}>{value}</span>;
};

const authHeaders = () => {
  const t = localStorage.getItem('keikka_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const fmtTs = (iso) => (iso ? String(iso).replace('T', ' ').slice(0, 16) : '—');

// -------------------- VERIFICATION QUEUE --------------------
const VerificationQueue = ({ l }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('pending');
  const [country, setCountry] = useState('');
  const [q, setQ] = useState('');
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status) params.status = status;
      if (country) params.country = country;
      if (q) params.q = q;
      const res = await axios.get(`${API}/api/admin/verification`, { headers: authHeaders(), params });
      setRows(res.data.items || []);
    } catch (e) {
      toast.error(l.actionFail);
    } finally {
      setLoading(false);
    }
  }, [status, country, q, l]);

  useEffect(() => { load(); }, [load]);

  if (selectedId) {
    return <VerificationDetail id={selectedId} onBack={() => { setSelectedId(null); load(); }} l={l} />;
  }

  return (
    <div className="space-y-3" data-testid="admin-verification-queue">
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="h-9 px-3 rounded-md border border-slate-300 bg-white text-sm"
          data-testid="verify-filter-status"
        >
          <option value="">{l.all}</option>
          <option value="pending">{l.pending}</option>
          <option value="approved">{l.approved}</option>
          <option value="rejected">{l.rejected}</option>
        </select>
        <Input
          placeholder={l.filterCountry}
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          className="h-9 w-28"
          data-testid="verify-filter-country"
        />
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder={l.searchPh}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="h-9 pl-9"
            data-testid="verify-search"
          />
        </div>
        <Button size="sm" variant="outline" onClick={load} data-testid="verify-refresh">
          <RefreshCw className="h-4 w-4 mr-1" /> {l.refresh}
        </Button>
      </div>

      {loading ? (
        <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : rows.length === 0 ? (
        <Card><CardContent className="py-8 text-center text-slate-500">{l.noRows}</CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b text-left text-xs font-semibold text-slate-500 uppercase">
                <tr>
                  <th className="px-4 py-2">{l.company}</th>
                  <th className="px-4 py-2">{l.country}</th>
                  <th className="px-4 py-2">{l.registry}</th>
                  <th className="px-4 py-2">{l.submitted}</th>
                  <th className="px-4 py-2">{l.status}</th>
                  <th className="px-4 py-2">{l.flags}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    onClick={() => setSelectedId(r.id)}
                    className="border-b hover:bg-orange-50 cursor-pointer"
                    data-testid={`verify-row-${r.id}`}
                  >
                    <td className="px-4 py-2 font-medium text-slate-900">{r.company_name}</td>
                    <td className="px-4 py-2 text-slate-700">{r.country}</td>
                    <td className="px-4 py-2 font-mono text-xs">{r.registry_id || '—'}</td>
                    <td className="px-4 py-2 text-slate-500 text-xs">{fmtTs(r.created_at)}</td>
                    <td className="px-4 py-2"><StatusBadge value={r.status} /></td>
                    <td className="px-4 py-2 text-xs">
                      {(r.risk_flags || []).map((f) => (
                        <Badge key={f} variant="outline" className="mr-1 bg-rose-50 text-rose-700 border-rose-200">{f}</Badge>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// -------------------- VERIFICATION DETAIL --------------------
const VerificationDetail = ({ id, onBack, l }) => {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/admin/verification/${id}`, { headers: authHeaders() });
        setDoc(res.data);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const approve = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/admin/verification/${id}/approve`, {}, { headers: authHeaders() });
      toast.success(l.actionOk);
      onBack();
    } catch (e) {
      toast.error(e.response?.data?.detail || l.actionFail);
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    if (reason.trim().length < 3) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/admin/verification/${id}/reject`, { reason }, { headers: authHeaders() });
      toast.success(l.actionOk);
      onBack();
    } catch (e) {
      toast.error(e.response?.data?.detail || l.actionFail);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!doc) return <div className="text-slate-500">—</div>;

  return (
    <div className="space-y-4" data-testid="admin-verification-detail">
      <Button variant="outline" size="sm" onClick={onBack} data-testid="verify-back">
        <ArrowLeft className="h-4 w-4 mr-1" /> {l.back}
      </Button>

      <Card>
        <CardContent className="py-4 space-y-3">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h2 className="text-xl font-bold">{doc.company_name}</h2>
              <p className="text-sm text-slate-500">
                {doc.country} · {doc.registry_id || '—'} · {fmtTs(doc.created_at)}
              </p>
            </div>
            <StatusBadge value={doc.status} />
          </div>
          <pre className="bg-slate-50 border rounded p-3 text-xs overflow-x-auto">
{JSON.stringify(doc.payload, null, 2)}
          </pre>
          {(doc.attachments || []).length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-slate-500 uppercase mb-1">Attachments</h4>
              <ul className="text-sm space-y-1">
                {doc.attachments.map((a, i) => (
                  <li key={i}>
                    <a href={a.url} target="_blank" rel="noreferrer" className="text-orange-600 hover:underline">
                      {a.label || a.url}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      {doc.status === 'pending' && (
        <>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={approve} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="verify-approve">
              <CheckCircle2 className="h-4 w-4 mr-1" /> {l.approve}
            </Button>
            <Button variant="outline" onClick={() => setRejectOpen((v) => !v)} data-testid="verify-reject-open">
              <XCircle className="h-4 w-4 mr-1" /> {l.reject}
            </Button>
          </div>
          {rejectOpen && (
            <Card>
              <CardContent className="py-4 space-y-3">
                <label className="block text-xs font-semibold text-slate-500 uppercase">{l.reasonLabel}</label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={l.reasonPh}
                  className="w-full border rounded p-2 text-sm min-h-[80px]"
                  data-testid="verify-reject-reason"
                />
                <div className="flex gap-2">
                  <Button onClick={reject} disabled={busy || reason.trim().length < 3} className="bg-rose-600 hover:bg-rose-700" data-testid="verify-reject-confirm">
                    {l.confirm}
                  </Button>
                  <Button variant="outline" onClick={() => { setRejectOpen(false); setReason(''); }}>
                    {l.cancel}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

// -------------------- OPTOUT QUEUE & DETAIL --------------------
const OptoutQueue = ({ l }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('pending');
  const [q, setQ] = useState('');
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status) params.status = status;
      if (q) params.q = q;
      const res = await axios.get(`${API}/api/admin/optout`, { headers: authHeaders(), params });
      setRows(res.data.items || []);
    } finally {
      setLoading(false);
    }
  }, [status, q]);

  useEffect(() => { load(); }, [load]);

  if (selectedId) {
    return <OptoutDetail id={selectedId} onBack={() => { setSelectedId(null); load(); }} l={l} />;
  }

  return (
    <div className="space-y-3" data-testid="admin-optout-queue">
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="h-9 px-3 rounded-md border border-slate-300 bg-white text-sm"
          data-testid="optout-filter-status"
        >
          <option value="">{l.all}</option>
          <option value="pending">{l.pending}</option>
          <option value="processed">{l.processed}</option>
          <option value="rejected">{l.rejected}</option>
        </select>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder={l.searchPh}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="h-9 pl-9"
            data-testid="optout-search"
          />
        </div>
        <Button size="sm" variant="outline" onClick={load} data-testid="optout-refresh">
          <RefreshCw className="h-4 w-4 mr-1" /> {l.refresh}
        </Button>
      </div>
      {loading ? (
        <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : rows.length === 0 ? (
        <Card><CardContent className="py-8 text-center text-slate-500">{l.noRows}</CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b text-left text-xs font-semibold text-slate-500 uppercase">
                <tr>
                  <th className="px-4 py-2">{l.company}</th>
                  <th className="px-4 py-2">{l.businessId}</th>
                  <th className="px-4 py-2">{l.requesterEmail}</th>
                  <th className="px-4 py-2">{l.submitted}</th>
                  <th className="px-4 py-2">{l.status}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} onClick={() => setSelectedId(r.id)} className="border-b hover:bg-orange-50 cursor-pointer" data-testid={`optout-row-${r.id}`}>
                    <td className="px-4 py-2 font-medium">{r.company_name}</td>
                    <td className="px-4 py-2 font-mono text-xs">{r.business_id || '—'}</td>
                    <td className="px-4 py-2 text-xs">{r.requester_email || '—'}</td>
                    <td className="px-4 py-2 text-slate-500 text-xs">{fmtTs(r.created_at)}</td>
                    <td className="px-4 py-2"><StatusBadge value={r.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const OptoutDetail = ({ id, onBack, l }) => {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [banOpen, setBanOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [dupeIds, setDupeIds] = useState('');
  const [banNote, setBanNote] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/admin/optout/${id}`, { headers: authHeaders() });
        setDoc(res.data);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const act = async (action, body) => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/admin/optout/${id}/${action}`, body || {}, { headers: authHeaders() });
      toast.success(l.actionOk);
      onBack();
    } catch (e) {
      toast.error(e.response?.data?.detail || l.actionFail);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!doc) return null;

  return (
    <div className="space-y-4" data-testid="admin-optout-detail">
      <Button variant="outline" size="sm" onClick={onBack} data-testid="optout-back">
        <ArrowLeft className="h-4 w-4 mr-1" /> {l.back}
      </Button>

      <Card>
        <CardContent className="py-4 space-y-3">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h2 className="text-xl font-bold">{doc.company_name}</h2>
              <p className="text-sm text-slate-500">
                {doc.business_id || '—'} · {doc.requester_email || '—'} · {fmtTs(doc.created_at)}
              </p>
            </div>
            <StatusBadge value={doc.status} />
          </div>
          <pre className="bg-slate-50 border rounded p-3 text-xs overflow-x-auto">
{JSON.stringify(doc.payload, null, 2)}
          </pre>
        </CardContent>
      </Card>

      {doc.status === 'pending' && (
        <>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={() => act('hide')} disabled={busy} className="bg-sky-600 hover:bg-sky-700" data-testid="optout-hide">
              <EyeOff className="h-4 w-4 mr-1" /> {l.hide}
            </Button>
            <Button onClick={() => act('delete')} disabled={busy} variant="outline" className="text-rose-700 border-rose-200 hover:bg-rose-50" data-testid="optout-delete">
              <Trash2 className="h-4 w-4 mr-1" /> {l.delete}
            </Button>
            <Button onClick={() => setBanOpen((v) => !v)} variant="outline" data-testid="optout-ban-open">
              <Flag className="h-4 w-4 mr-1" /> {l.banDupes}
            </Button>
            <Button onClick={() => setRejectOpen((v) => !v)} variant="outline" data-testid="optout-reject-open">
              <XCircle className="h-4 w-4 mr-1" /> {l.reject}
            </Button>
          </div>

          {rejectOpen && (
            <Card>
              <CardContent className="py-4 space-y-3">
                <label className="block text-xs font-semibold text-slate-500 uppercase">{l.reasonLabel}</label>
                <textarea value={reason} onChange={(e) => setReason(e.target.value)}
                  className="w-full border rounded p-2 text-sm min-h-[80px]" data-testid="optout-reject-reason" />
                <div className="flex gap-2">
                  <Button onClick={() => act('reject', { reason })} disabled={busy || reason.trim().length < 3}
                    className="bg-rose-600 hover:bg-rose-700" data-testid="optout-reject-confirm">
                    {l.confirm}
                  </Button>
                  <Button variant="outline" onClick={() => { setRejectOpen(false); setReason(''); }}>{l.cancel}</Button>
                </div>
              </CardContent>
            </Card>
          )}

          {banOpen && (
            <Card>
              <CardContent className="py-4 space-y-3">
                <label className="block text-xs font-semibold text-slate-500 uppercase">{l.duplicateIds}</label>
                <Input value={dupeIds} onChange={(e) => setDupeIds(e.target.value)}
                  placeholder="biz_001, biz_002" data-testid="optout-ban-ids" />
                <label className="block text-xs font-semibold text-slate-500 uppercase">{l.note}</label>
                <Input value={banNote} onChange={(e) => setBanNote(e.target.value)} data-testid="optout-ban-note" />
                <div className="flex gap-2">
                  <Button onClick={() => act('ban-duplicates', {
                    note: banNote || undefined,
                    duplicate_ids: dupeIds.split(',').map((s) => s.trim()).filter(Boolean),
                  })} disabled={busy} data-testid="optout-ban-confirm">
                    {l.confirm}
                  </Button>
                  <Button variant="outline" onClick={() => { setBanOpen(false); setDupeIds(''); setBanNote(''); }}>
                    {l.cancel}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

// -------------------- AUDIT LOG --------------------
const AuditLog = ({ l }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/admin/audit?limit=100`, { headers: authHeaders() });
        setRows(res.data.items || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <Card data-testid="admin-audit-log">
      <CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b text-left text-xs font-semibold text-slate-500 uppercase">
            <tr>
              <th className="px-4 py-2">{l.auditTime}</th>
              <th className="px-4 py-2">{l.auditAction}</th>
              <th className="px-4 py-2">{l.auditEntity}</th>
              <th className="px-4 py-2">{l.auditActor}</th>
              <th className="px-4 py-2">Metadata</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b hover:bg-slate-50" data-testid={`audit-row-${r.id}`}>
                <td className="px-4 py-2 text-xs text-slate-500 whitespace-nowrap">{fmtTs(r.created_at)}</td>
                <td className="px-4 py-2 font-mono text-xs">{r.action_type}</td>
                <td className="px-4 py-2 text-xs">{r.entity_type} · {r.entity_id}</td>
                <td className="px-4 py-2 text-xs">{r.admin_email || r.admin_user_id || '—'}</td>
                <td className="px-4 py-2 text-xs text-slate-500 max-w-md truncate">
                  {r.metadata ? JSON.stringify(r.metadata) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
};

// -------------------- SHARE LIVE DIALOG --------------------
const ShareLiveDialog = ({ open, onOpenChange, country, l }) => {
  const [busy, setBusy] = useState(false);
  const [share, setShare] = useState(null); // {url, qr_png_data_url, expires_at, share_id}
  const [copied, setCopied] = useState(false);

  const createShare = useCallback(async () => {
    setBusy(true);
    setCopied(false);
    try {
      const res = await axios.post(
        `${API}/api/admin/demo-share/create`,
        { country: (country || 'NO').toUpperCase() },
        { headers: authHeaders() },
      );
      setShare(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || l.actionFail);
    } finally {
      setBusy(false);
    }
  }, [country, l]);

  // Create a fresh share link whenever the dialog opens (or country changes).
  useEffect(() => {
    if (open) createShare();
    else setShare(null);
  }, [open, createShare]);

  const onCopy = async () => {
    if (!share?.url) return;
    try {
      await navigator.clipboard.writeText(share.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      // Fallback: temporary textarea
      const ta = document.createElement('textarea');
      ta.value = share.url;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (_) {}
      document.body.removeChild(ta);
    }
  };

  const onDownloadQr = () => {
    if (!share?.qr_png_data_url) return;
    const a = document.createElement('a');
    a.href = share.qr_png_data_url;
    a.download = `keikka-live-demo-${country || 'NO'}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const expiresLabel = share?.expires_at
    ? new Date(share.expires_at).toLocaleString()
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="share-live-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-4 w-4 text-orange-600" />
            {l.shareLiveTitle}
          </DialogTitle>
          <DialogDescription>{l.shareLiveDesc}</DialogDescription>
        </DialogHeader>

        {busy && !share ? (
          <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : share ? (
          <div className="space-y-4">
            <div className="flex justify-center">
              <img
                src={share.qr_png_data_url}
                alt="Live demo QR code"
                className="w-52 h-52 rounded-lg border border-slate-200 bg-white"
                data-testid="share-qr-img"
              />
            </div>

            <div className="flex items-center gap-2">
              <Input
                readOnly
                value={share.url}
                className="font-mono text-xs"
                data-testid="share-url-input"
                onFocus={(e) => e.target.select()}
              />
              <Button
                size="sm"
                variant="outline"
                onClick={onCopy}
                data-testid="share-copy-btn"
                title={copied ? l.shareCopied : l.shareCopy}
              >
                {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>

            <div className="flex items-center justify-between text-xs text-slate-500">
              <span data-testid="share-expires">
                {l.shareExpires}: <span className="font-mono text-slate-700">{expiresLabel}</span>
              </span>
              <span className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold">
                {l.shareValidFor}
              </span>
            </div>

            <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-slate-100">
              <Button
                size="sm"
                variant="outline"
                onClick={onDownloadQr}
                data-testid="share-download-qr"
              >
                <Download className="h-4 w-4 mr-1" /> {l.shareDownloadQr}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={createShare}
                disabled={busy}
                data-testid="share-regenerate"
              >
                <RefreshCw className={`h-4 w-4 mr-1 ${busy ? 'animate-spin' : ''}`} /> {l.shareRegenerate}
              </Button>
              <a
                href={share.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center h-9 px-3 rounded-md border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm"
                data-testid="share-open-link"
              >
                {l.shareOpen} →
              </a>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

// -------------------- DEMO OPENS (QR scan analytics) --------------------

// Admin-side 🏆 Peak overlay. Same trigger data as the public /live page
// (reads from the shared `live_peak_state` doc) so both screens fire in
// lock-step — "same wire" effect during the Norway demo.
const AdminPeakSticker = ({ peakAt, peakValue, l }) => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!peakAt) return;
    let shown = null;
    try { shown = sessionStorage.getItem('keikka_admin_peak_shown_at'); } catch (_) {}
    if (shown === peakAt) return;
    try { sessionStorage.setItem('keikka_admin_peak_shown_at', peakAt); } catch (_) {}
    setVisible(true);
    const id = setTimeout(() => setVisible(false), 8000);
    return () => clearTimeout(id);
  }, [peakAt]);
  if (!visible) return null;
  return (
    <div
      className="fixed top-4 right-4 z-[60] bg-gradient-to-br from-amber-400 to-amber-600 text-white rounded-2xl shadow-xl border border-amber-300 px-5 py-3 flex items-center gap-3"
      data-testid="admin-peak-sticker"
      role="status"
      aria-live="polite"
    >
      <span className="text-3xl leading-none" aria-hidden="true">🏆</span>
      <div>
        <div className="font-black text-base leading-tight" data-testid="admin-peak-title">{l.peakTitle}</div>
        <div className="text-[11px] font-semibold opacity-90">
          {l.peakSub} · <span className="font-mono tabular-nums">{peakValue}</span>
        </div>
      </div>
    </div>
  );
};

// Tiny inline SVG sparkline — no chart libs.
// `data` = [{t_start, t_end, count}]
const Sparkline = ({ data = [], width = 140, height = 28 }) => {
  if (!data.length) return <svg width={width} height={height} />;
  const max = Math.max(1, ...data.map((d) => d.count || 0));
  const step = width / Math.max(1, data.length - 1);
  const padTop = 2;
  const padBot = 2;
  const h = height - padTop - padBot;
  const points = data.map((d, i) => {
    const x = i * step;
    const y = padTop + h - ((d.count || 0) / max) * h;
    return [x, y];
  });
  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  const areaD = `${pathD} L${(points[points.length - 1][0]).toFixed(1)},${(height - padBot).toFixed(1)} L0,${(height - padBot).toFixed(1)} Z`;
  const fmtClock = (iso) => {
    try {
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, '0');
      return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    } catch (e) { return iso; }
  };
  return (
    <svg width={width} height={height} data-testid="demos-sparkline" role="img" aria-label="demo opens last 5 minutes">
      <path d={areaD} fill="rgba(16,185,129,0.12)" />
      <path d={pathD} fill="none" stroke="#10b981" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      {points.map(([x, y], i) => (
        <g key={i}>
          <circle cx={x} cy={y} r={data[i].count > 0 ? 2 : 1.2} fill={data[i].count > 0 ? '#10b981' : '#94a3b8'} />
          <rect x={x - step / 2} y={0} width={step} height={height} fill="transparent">
            <title>{`${fmtClock(data[i].t_start)}–${fmtClock(data[i].t_end)}: ${data[i].count}`}</title>
          </rect>
        </g>
      ))}
    </svg>
  );
};

// "Mozilla/5.0 (iPhone; …) Safari/604" → "Safari · mobile"
const shortUA = (ua) => {
  if (!ua) return '—';
  const s = String(ua);
  let browser = 'Other';
  if (/Edg\//i.test(s)) browser = 'Edge';
  else if (/Chrome\//i.test(s) && !/Edg/.test(s)) browser = 'Chrome';
  else if (/Safari\//i.test(s) && !/Chrome/.test(s)) browser = 'Safari';
  else if (/Firefox\//i.test(s)) browser = 'Firefox';
  let os = '';
  if (/iPhone|iPad|iPod/.test(s)) os = 'iOS';
  else if (/Android/.test(s)) os = 'Android';
  else if (/Mac OS X/.test(s)) os = 'macOS';
  else if (/Windows/.test(s)) os = 'Win';
  else if (/Linux/.test(s)) os = 'Linux';
  return os ? `${browser} · ${os}` : browser;
};

const DemoOpens = ({ l }) => {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [series, setSeries] = useState([]);
  const [lastEventAt, setLastEventAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('idle'); // idle | live | offline
  const [lastOkAt, setLastOkAt] = useState(null);
  const [country, setCountry] = useState(() => {
    // Preselect from URL ?country=NO — handy when the Norway demo QR
    // routes stakeholders straight to the admin dashboard.
    try {
      const u = new URL(window.location.href);
      return (u.searchParams.get('country') || '').toUpperCase();
    } catch (e) { return ''; }
  });
  const [shareOpen, setShareOpen] = useState(false);
  const [peak, setPeak] = useState(null);
  // QA-only flag: when true, force fetches to fail (`localStorage.demos_force_offline = '1'`).
  const forceOfflineRef = React.useRef(false);
  useEffect(() => {
    try { forceOfflineRef.current = localStorage.getItem('demos_force_offline') === '1'; } catch (e) {}
  }, []);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      // Re-read the QA flag on every call so a tester can flip it on/off
      // from devtools without reloading.
      try { forceOfflineRef.current = localStorage.getItem('demos_force_offline') === '1'; } catch (e) {}
      if (forceOfflineRef.current) throw new Error('forced-offline');

      const params = { limit: 50, window: '5m' };
      if (country) params.country = country;
      const res = await axios.get(`${API}/api/admin/demos`, { headers: authHeaders(), params });
      setRows(res.data.items || []);
      setSummary(res.data.summary || null);
      setSeries(res.data.series || []);
      setLastEventAt(res.data.last_event_at || null);
      setPeak(res.data.peak || null);
      setStatus('live');
      setLastOkAt(new Date());
    } catch (e) {
      setStatus('offline');
      if (!silent) toast.error(l.actionFail);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [l, country]);

  // Initial + on-country-change load
  useEffect(() => { load(); }, [load]);

  // Background auto-refresh every 15s (silent; no full-page reload).
  // Pauses when the tab is hidden to avoid burning cycles in the
  // background and re-fires immediately on visibilitychange=visible.
  useEffect(() => {
    const tick = () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      load({ silent: true });
    };
    const id = setInterval(tick, 15000);
    const onVis = () => {
      if (!document.hidden) load({ silent: true });
    };
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(id);
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVis);
    };
  }, [load]);

  const deviceIcon = (d) => {
    if (!d) return '—';
    if (d === 'mobile') return '📱 mobile';
    if (d === 'desktop') return '🖥 desktop';
    return d;
  };

  const fmtClock = (d) => {
    if (!d) return '—';
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };

  const fmtIsoClock = (iso) => {
    if (!iso) return '—';
    try { return fmtClock(new Date(iso)); } catch (e) { return '—'; }
  };

  const last5m = summary?.last_window ?? 0;

  return (
    <div className="space-y-3" data-testid="admin-demos-queue">
      {/* 🏆 Admin Peak overlay — fires on new all-time 5-min record,
          server-side cooldown 10 min + MIN_TRIGGER 8 weighted gates it. */}
      {peak?.is_new_peak && (
        <AdminPeakSticker
          peakAt={peak.last_peak_at || peak.now}
          peakValue={peak.peak_5m_all_time}
          l={l}
        />
      )}

      {/* Top row: filter (left) · LIVE + sparkline + last update (right) */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{l.demosCountry}</label>
          <select
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            className="h-8 px-2 rounded-md border border-slate-300 bg-white text-sm"
            data-testid="demos-country-filter"
          >
            <option value="">{l.all}</option>
            <option value="NO">NO</option>
            <option value="EE">EE</option>
            <option value="FI">FI</option>
            <option value="SE">SE</option>
          </select>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          {/* 5m sparkline + label */}
          <div className="flex items-center gap-2 px-2 py-1 rounded-md border border-slate-200 bg-white" title="Demo opens — last 5 minutes, 30s buckets">
            <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">5m</span>
            <Sparkline data={series} />
            <span className="text-xs font-semibold text-emerald-700" data-testid="demos-last-5m">{last5m}</span>
          </div>

          {/* Last update clock (always visible) */}
          <div className="text-xs text-slate-500" data-testid="demos-last-update">
            {l.demosLastUpdate}: <span className="font-mono text-slate-700">{fmtClock(lastOkAt)}</span>
          </div>

          {/* LIVE / OFFLINE pill */}
          {status === 'offline' ? (
            <div
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-slate-300 bg-slate-100 text-slate-600 text-xs font-semibold"
              data-testid="demos-live-indicator-offline"
              title="Auto-refresh paused — last fetch failed"
            >
              <span className="h-2 w-2 rounded-full bg-slate-400" />
              OFFLINE{lastOkAt ? ` · ${fmtClock(lastOkAt)}` : ''}
            </div>
          ) : (
            <div
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 text-xs font-semibold"
              data-testid="demos-live-indicator"
              title="Table auto-refreshes in the background every 15 seconds"
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
              </span>
              LIVE · auto-refresh 15s
            </div>
          )}
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
          <StatCard label={l.demosLast5m} value={last5m} tone="emerald" />
          <StatCard label={l.demosPeakAllTime} value={peak?.peak_5m_all_time ?? 0} tone="amber" />
          <StatCard label={l.demosLast24h} value={summary.last_24h ?? 0} tone="emerald" />
          <StatCard label={l.demosTotal} value={summary.total ?? 0} tone="sky" />
          <StatCard label={l.demosTopCountry} value={summary.top_country || '—'} tone="amber" />
          <StatCard label={l.demosShown} value={summary.shown ?? 0} tone="slate" />
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" variant="outline" onClick={() => load()} data-testid="demos-refresh">
          <RefreshCw className="h-4 w-4 mr-1" /> {l.refresh}
        </Button>
        <Button
          size="sm"
          className="bg-orange-600 hover:bg-orange-700 text-white"
          onClick={() => setShareOpen(true)}
          data-testid="demos-share-open"
        >
          <Share2 className="h-4 w-4 mr-1" /> {l.shareLive}
        </Button>
        {lastEventAt && (
          <span className="text-xs text-slate-500">
            {l.demosLastEvent}: <span className="font-mono text-slate-700">{fmtIsoClock(lastEventAt)}</span>
          </span>
        )}
      </div>

      <ShareLiveDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        country={country || 'NO'}
        l={l}
      />

      {loading ? (
        <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : rows.length === 0 ? (
        <Card><CardContent className="py-8 text-center text-slate-500">{l.demosEmpty}</CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b text-left text-xs font-semibold text-slate-500 uppercase">
                <tr>
                  <th className="px-4 py-2">{l.auditTime}</th>
                  <th className="px-4 py-2">{l.demosCountry}</th>
                  <th className="px-4 py-2">{l.demosLang}</th>
                  <th className="px-4 py-2">{l.demosDevice}</th>
                  <th className="px-4 py-2">{l.demosUa}</th>
                  <th className="px-4 py-2">{l.demosPath}</th>
                  <th className="px-4 py-2">{l.demosSession}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.event_id} className="border-b hover:bg-orange-50" data-testid={`demos-row-${r.event_id}`}>
                    <td className="px-4 py-2 text-xs text-slate-500 whitespace-nowrap">{fmtTs(r.ts)}</td>
                    <td className="px-4 py-2 font-semibold">
                      {r.country ? (
                        <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">{r.country}</Badge>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs">{r.lang || '—'}</td>
                    <td className="px-4 py-2 text-xs">{deviceIcon(r.device)}</td>
                    <td className="px-4 py-2 text-xs text-slate-600" title={r.user_agent || ''}>{shortUA(r.user_agent)}</td>
                    <td className="px-4 py-2 text-xs text-slate-500 max-w-xs truncate" title={r.path || ''}>{r.path || '—'}</td>
                    <td className="px-4 py-2 font-mono text-xs text-slate-500 max-w-[200px] truncate" title={r.session_id || ''}>{r.session_id || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// -------------------- STATS + SHELL --------------------
const StatCard = ({ label, value, tone = 'slate' }) => {
  const c = {
    slate: 'bg-slate-50 text-slate-700',
    amber: 'bg-amber-50 text-amber-700',
    emerald: 'bg-emerald-50 text-emerald-700',
    rose: 'bg-rose-50 text-rose-700',
    sky: 'bg-sky-50 text-sky-700',
  }[tone];
  return (
    <div className={`${c} rounded-lg p-4`}>
      <div className="text-xs font-semibold uppercase tracking-wide opacity-80">{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
};

const AdminP1Page = () => {
  const l = useT();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/admin/p1/stats`, { headers: authHeaders() })
      .then((r) => setStats(r.data))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <GlobalNav />
      <div className="max-w-[1200px] mx-auto px-4 py-6 space-y-6">
        <h1 className="text-2xl font-bold text-slate-900">{l.title}</h1>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
            <StatCard label={`${l.tabVerify}: ${l.pending}`} value={stats.verification.pending} tone="amber" />
            <StatCard label={l.approvedToday} value={stats.verification.approved_today} tone="emerald" />
            <StatCard label={l.rejectedToday} value={stats.verification.rejected_today} tone="rose" />
            <StatCard label={`${l.tabOptout}: ${l.pending}`} value={stats.optout.pending} tone="amber" />
            <StatCard label={l.processedToday} value={stats.optout.processed_today} tone="sky" />
            <StatCard label={l.rejectedToday} value={stats.optout.rejected_today} tone="rose" />
          </div>
        )}

        <AdminBudgetStatsWidget days={30} />
        <AdminBackhaulRateWidget days={7} />

        <Tabs defaultValue="verify">
          <TabsList>
            <TabsTrigger value="verify" data-testid="tab-verify">{l.tabVerify}</TabsTrigger>
            <TabsTrigger value="optout" data-testid="tab-optout">{l.tabOptout}</TabsTrigger>
            <TabsTrigger value="audit" data-testid="tab-audit">{l.tabAudit}</TabsTrigger>
            <TabsTrigger value="demos" data-testid="tab-demos">
              <QrCode className="h-4 w-4 mr-1" /> {l.tabDemos}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="verify"><VerificationQueue l={l} /></TabsContent>
          <TabsContent value="optout"><OptoutQueue l={l} /></TabsContent>
          <TabsContent value="audit"><AuditLog l={l} /></TabsContent>
          <TabsContent value="demos"><DemoOpens l={l} /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminP1Page;
